import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, errMsg } from "../lib/api";
import { useLang } from "../lib/i18n";
import { APPT_TYPES, APPT_STATUSES } from "../lib/constants";
import { todayStr } from "../lib/helpers";
import { Field, Options, useBusy } from "./common";
import { AutofillButton } from "./ai/AutofillDialog";
import CustomerPicker from "./CustomerPicker";

export default function AppointmentForm({ initial, onClose, onSaved }) {
  const { t, lang } = useLang();
  const [f, setF] = useState({
    customer_id: "", address: "", date: todayStr(), time: "09:00", type: "estimate", notes: "",
    assigned_to: "", status: "scheduled", ...(initial || {}),
  });
  const [users, setUsers] = useState([]);
  const [busy, run] = useBusy();
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data.filter((u) => u.active))).catch(() => {});
  }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = (e) =>
    run(async () => {
      e.preventDefault();
      if (!f.customer_id) return toast.error(t("Select a customer", "Seleccione un cliente"));
      const body = { ...f, assigned_to: f.assigned_to || null };
      try {
        const { data } = initial?.id ? await api.put(`/appointments/${initial.id}`, body) : await api.post("/appointments", body);
        toast.success(t("Appointment saved", "Cita guardada"));
        onSaved?.(data);
        onClose();
      } catch (err) {
        toast.error(errMsg(err));
      }
    });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="appointment-form-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? t("Edit Appointment", "Editar cita") : t("New Appointment", "Nueva cita")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="grid gap-3">
          <AutofillButton form="appointment" current={f} ctx={{ customer_id: f.customer_id, project_id: f.project_id }} onApply={(v) => setF((x) => ({ ...x, ...v }))} className="w-full h-11" />
          <Field label={t("Customer *", "Cliente *")}>
            <CustomerPicker value={f.customer_id} onChange={(id, c) => setF({ ...f, customer_id: id, address: f.address || c?.property_address || "" })} testid="appointment-form-customer" />
          </Field>
          <Field label={t("Address", "Dirección")}>
            <input className="inp" value={f.address} onChange={set("address")} data-testid="appointment-form-address" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("Date", "Fecha")}>
              <input className="inp" type="date" required value={f.date} onChange={set("date")} data-testid="appointment-form-date" />
            </Field>
            <Field label={t("Time", "Hora")}>
              <input className="inp" type="time" value={f.time} onChange={set("time")} data-testid="appointment-form-time" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("Type", "Tipo")}>
              <select className="inp" value={f.type} onChange={set("type")} data-testid="appointment-form-type">
                <Options items={APPT_TYPES} lang={lang} />
              </select>
            </Field>
            <Field label={t("Status", "Estado")}>
              <select className="inp" value={f.status} onChange={set("status")} data-testid="appointment-form-status">
                <Options items={APPT_STATUSES} lang={lang} />
              </select>
            </Field>
          </div>
          <Field label={t("Assigned employee", "Empleado asignado")}>
            <select className="inp" value={f.assigned_to || ""} onChange={set("assigned_to")} data-testid="appointment-form-assigned">
              <option value="">{t("Not assigned", "Sin asignar")}</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>
          <Field label={t("Notes", "Notas")} ai={{ form: "appointment", field: "notes", value: f.notes, onInsert: (v) => setF((x) => ({ ...x, notes: v })), ctx: { customer_id: f.customer_id } }}>
            <textarea className="inp" value={f.notes} onChange={set("notes")} data-testid="appointment-form-notes" />
          </Field>
          <button className="btn-primary w-full" disabled={busy} data-testid="appointment-form-save-btn">
            {t("Save Appointment", "Guardar cita")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
